"use client";

import { cn, Button } from "@oreme/ui";
import { cva } from "class-variance-authority";
import React, { type ReactElement } from "react";
import {
  HomeTeaserProject,
  HomeTeaserGallery,
  HomeTeaserGlossary,
  HomeSectionHeader,
} from "@/components";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

const sectionListItemVariants = cva(
  "flex flex-col transform transition-transform scale-100 hover:drop-shadow-lg hover:-translate-y-2 hover:scale-102",
);

function HomeSectionTeaser(): ReactElement {
  const t = useTranslations("home.SectionTeaser");
  const animation = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <section>
      <div className="relative max-w-screen-2xl mx-auto flex flex-col py-12">
        <HomeSectionHeader title={t("title")} lead={t("lead")} />
        <ul className="relative z-20 grid gap-4 px-4 grid-cols-1 md:grid-cols-3">
          <motion.li
            key={0}
            className={cn(sectionListItemVariants())}
            initial={animation.initial}
            animate={animation.animate}
            transition={{
              duration: 0.4,
              delay: 0,
              scale: { type: "spring", visualDuration: 0.4, bounce: 0.5 },
            }}
            whileInView="onscreen"
          >
            <HomeTeaserProject />
          </motion.li>
          <motion.li
            key={1}
            className={cn(sectionListItemVariants())}
            initial={animation.initial}
            animate={animation.animate}
            transition={{
              duration: 0.4,
              delay: 0.2,
              scale: { type: "spring", visualDuration: 0.4, bounce: 0.5 },
            }}
            whileInView="onscreen"
          >
            <HomeTeaserGallery />
          </motion.li>
          <motion.li
            key={2}
            className={cn(sectionListItemVariants())}
            initial={animation.initial}
            animate={animation.animate}
            transition={{
              duration: 0.4,
              delay: 0.4,
              scale: { type: "spring", visualDuration: 0.4, bounce: 0.5 },
            }}
            whileInView="onscreen"
          >
            <HomeTeaserGlossary />
          </motion.li>
        </ul>
        <footer className="text-center mt-8 px-4">
          <Button className="border-0 flex sm:inline-flex" asChild>
            <Link href="/more">
              {t("action")} <ArrowRight strokeWidth={1} />
            </Link>
          </Button>
        </footer>
      </div>
    </section>
  );
}

export { HomeSectionTeaser };
